import { ReactNode, useEffect, useState } from "react";
import { ThemeProvider, HostThemeProvider, ThemeProviderProps } from "@kaltura/ds-react-theme";
import { CustomizationsProvider } from "@kaltura/ds-react-utils";
import { kmsCookie, booleanProp } from "@kaltura/mediaspace-shared-utils";

const components = {
    button: {
        globalClasses: {
            props: ["variant", "color", "loading", "size", "disabled"],
            booleanTrueValue: "on",
            booleanFalseValue: "off",
        },
    },
};

export type ThemeProviderScope =
    | {
          scoped: true;
          // cssPrefix prop is mandatory for scoped components
          cssPrefix: string;
      }
    | {
          scoped?: false;
          // cssPrefix prop doesn't exist for non-scoped components
      };

type KMSThemeProviderProps = Omit<ThemeProviderProps, "cssPrefix"> & ThemeProviderScope & { children: ReactNode };

/**
 * KMS Components Theme Provider - a wrapper to kaltura-ds ThemeProvider,
 * with CustomizationsProvider set to kms customization requirements.
 *
 * Provide the cssPrefix prop to render a scoped theme provider,
 * skip it to render the main theme provider for the hosting app.
 */
const KMSThemeProvider = (props: KMSThemeProviderProps) => {
    const { children, ...rest } = props;
    const [highContrast, setHighContrast] = useState(booleanProp(kmsCookie("kms-contrast") as string));

    const setHighContrastCookie = () => {
        setHighContrast(booleanProp(kmsCookie("kms-contrast") as string));
    };

    // subscribe to high contrast event (dispatched by a menu item)
    useEffect(() => {
        document.addEventListener("kms-contrast", setHighContrastCookie);

        return () => {
            document.removeEventListener("kms-contrast", setHighContrastCookie);
        };
    }, []);

    const contents = <CustomizationsProvider components={components}>{children}</CustomizationsProvider>;

    // Report missing cssPrefix prop only once
    const missingCssPrefix = props.scoped && !props.cssPrefix;
    useEffect(() => {
        if (missingCssPrefix) {
            console.warn("cssPrefix prop is missing for a scoped ThemeProvider!");
        }
    }, [missingCssPrefix]);

    return props.scoped ? (
        <ThemeProvider {...rest} cssPrefix={props.cssPrefix} highContrast={highContrast}>
            {contents}
        </ThemeProvider>
    ) : (
        <HostThemeProvider {...rest} highContrast={highContrast}>
            {contents}
        </HostThemeProvider>
    );
};

export { KMSThemeProvider as ThemeProvider };
